import { cva, type VariantProps } from "../../lib";
import { minTouchTargetClassName } from "../../lib/touch-target";
import type { IconColor } from "../icons/types";

/**
 * CVA variants for the Select trigger container.
 *
 * Mirrors textFieldContainerVariants' sizes: Figma's Select field is an
 * instance of TextField's field (8063:9447 draws small and medium), so it takes
 * the same desktop step (`md:`) in height, padding and radius.
 * - "open" replaces "focused" (Select has no text-focus state)
 */
export const selectTriggerVariants = cva(
  [
    // Web doesn't clip, so the touch areas below can pass the trigger's edge;
    // the value truncates on its own (line-clamp-1)
    "native:flex gap-spacing-sm border-luce-neutral-border-mediumEmphasis bg-luce-neutral-background-default native:overflow-hidden relative flex flex-row items-center outline-none",
    // Web keyboard focus: the ring Chip, Switch and Checkbox draw
    "web:focus-visible:ring-luce-brand-primaryBorder web:focus-visible:ring-focus web:focus-visible:ring-offset-focus",
    // Touch screens (web): the small 40px trigger takes taps 2px past its top and
    // bottom, to 44; the medium one is 48 already, so this adds nothing there
    minTouchTargetClassName,
  ].join(" "),
  {
    variants: {
      size: {
        sm: "rounded-radius-md md:rounded-radius-lg px-spacing-md md:px-spacing-lg py-spacing-sm md:py-spacing-md h-field-sm md:h-field-sm-dt",
        md: "rounded-radius-lg md:rounded-radius-xl px-spacing-md md:px-spacing-lg py-spacing-md md:py-spacing-lg h-field-md md:h-field-md-dt",
      },
      visualState: {
        // 1px at rest, 1.5px open or in error, as TextField (Figma border-1 / border-1-5)
        default: "hover:border-luce-neutral-border-highEmphasis border-1",
        open: "border-luce-brand-primaryBorder border-1-5",
        error: "border-luce-system-danger-default border-1-5",
        // mediumEmphasis, as the disabled TextField: a disabled field recedes
        // (designer, 2026-10-29)
        disabled: [
          "border-luce-neutral-border-mediumEmphasis border-1",
          "bg-luce-neutral-background-intermediate",
        ].join(" "),
      },
    },
    defaultVariants: {
      size: "sm",
      visualState: "default",
    },
  },
);

export type SelectTriggerVariantsProps = VariantProps<typeof selectTriggerVariants>;

/**
 * CVA for the trigger's icon colour (leading icon + chevron).
 */
export const selectTriggerIconVariants = cva("", {
  variants: {
    disabled: {
      true: "text-luce-neutral-foreground-attenuated",
      false: "text-luce-neutral-foreground-intermediate",
    },
  },
  defaultVariants: { disabled: false },
});

/**
 * Each option row's label and icon colour. A disabled row is attenuated, even
 * when it's the selected one.
 */
export const selectItemColor = {
  default: "neutral-foreground-default",
  selected: "brand-primary",
  disabled: "neutral-foreground-attenuated",
} as const satisfies Record<"default" | "selected" | "disabled", IconColor>;

/**
 * Classname for the Select dropdown content container.
 */
export const selectContentVariants = [
  "z-50 overflow-hidden",
  // 12 = the options' 8 + the 4px inset, so the corners nest (Figma 4759:20482)
  "rounded-radius-lg",
  "border-luce-neutral-border-mediumEmphasis border-1",
  "bg-luce-neutral-background-default",
  "shadow-dropdown",
  "p-spacing-xs",
  // Web open/close animation
  "web:data-[state=open]:animate-in web:data-[state=open]:fade-in-0 web:data-[state=open]:zoom-in-95",
  "web:data-[state=closed]:animate-out web:data-[state=closed]:fade-out-0 web:data-[state=closed]:zoom-out-95",
  // Popper: shift down slightly from trigger
  "web:data-[side=bottom]:translate-y-spacing-xs",
].join(" ");

/**
 * CVA variants for each option row inside the dropdown.
 */
export const selectItemVariants = cva(
  [
    "flex w-full flex-row items-center",
    // Figma's option row (4759:20342), one step up on desktop
    "px-spacing-lg md:px-spacing-xl py-spacing-sm md:py-spacing-md gap-spacing-md md:gap-spacing-lg",
    "rounded-radius-md",
    "outline-none",
    "transition-colors",
    "cursor-pointer",
  ].join(" "),
  {
    variants: {
      selected: {
        true: "bg-luce-brand-primarySurface",
        false: [
          "bg-transparent",
          "web:hover:bg-luce-neutral-background-intermediate",
          // The option the arrow keys move to: Radix focuses it
          "web:focus:bg-luce-neutral-background-intermediate",
          "active:bg-luce-neutral-background-intermediate",
        ].join(" "),
      },
      disabled: {
        // The attenuated text already says disabled; no extra fade
        true: "web:cursor-not-allowed pointer-events-none",
        false: "",
      },
    },
    defaultVariants: {
      selected: false,
      disabled: false,
    },
  },
);
